import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Tab,
  TabList,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  DismissRegular,
  PhoneRegular,
  EyeOffRegular,
  EyeRegular,
  SpeakerOffRegular,
  Speaker2Regular,
  EditRegular,
  PaymentRegular,
  SearchRegular,
  FilterRegular,
  ErrorCircleRegular,
  ArchiveRegular,
} from '@fluentui/react-icons';
import { Tender, Tendering, TenderManagerTool as TenderManagerToolModel } from '../../../models/tendering';
import { UserSystemData } from '../../../models/users';
import { useAppContext } from '../../../AppContext';
import PasswordDialog from '../../dialogs/PasswordDialog';
import { getSystemOptionsByID } from '../../../services/systemOptionService';
import { Tag } from '../../../models/tag';
import authService from '../../../services/authService';
import { getTenderDocument, updateTenderDocument, updateTenderManagerTool } from '../../../services/tenderingService';
import * as tenderingService from '../../../services/tenderingService';
import TenderContactDrawer from '../../drawers/TenderContactDrawer';
import { PlannedDialog } from '../Dialogs/TenderingManagerDialogs/PlannedDialog';
import { FollowedUpDialog } from '../Dialogs/TenderingManagerDialogs/FollowedUpDialog';
import { TenderingManagerDialog } from '../Dialogs/TenderingManagerDialogs/TenderingManagerDialog';
import { StatusesDialog } from '../Dialogs/TenderingManagerDialogs/StatusesDialog';
import NewNote from '../Dialogs/NewNote';
import { createNote } from '../../../services/noteService';
import TenderingManagerFilterDialog from '../../dialogs/TenderingManagerFilterDialog';
import { getModule, updateModule } from '../../../services/moduleService';
import { Module } from '../../../models/module';
// import { TenderContactModal } from '../tender-contact-modal/TenderContactModal';
// import { TenderingManagerFilterModal } from '../tendering-manager-filter-modal/TenderingManagerFilterModal';
// import { PlannedDialog } from './dialogs/PlannedDialog';
// import { FollowedUpDialog } from './dialogs/FollowedUpDialog';
// import { TenderingManagerDialog } from './dialogs/TenderingManagerDialog';
// import { PrioDialog } from './dialogs/PrioDialog';
// import { StatusesDialog } from './dialogs/StatusesDialog';

interface TenderManagerModalProps {
  // onDismiss: (filters?: string[]) => void;
  tenderDocument?: Tender;
  tenderManagerTool?: TenderManagerToolModel;
  tenderingDocument?: Tendering;
  users?: any[];
  tags?: Tag[];
  onChange?: (tenderManagerTool: TenderManagerToolModel) => void;
  onFilterAdd?: (filter: string) => void;
  onTenderChange?: (patch: Partial<Tender>) => void;
}

export const TenderManagerModal: React.FC<TenderManagerModalProps> = ({
  // onDismiss,
  tenderDocument: initialTender,
  tenderManagerTool: initialTenderManagerTool,
  tenderingDocument: tendering,
  users,
  tags,
  onChange,
  onFilterAdd,
  onTenderChange,
}) => {
  const { user } = useAppContext();
  const [tender, setTender] = useState<Tender | null>(null);
  const [tenderName, setTenderName] = useState<string>('');
  const [systemPassword, setSystemPassword] = useState<string>('');
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [tenderManagerTool, setTenderManagerTool] = useState<TenderManagerToolModel | null>(null);
  const [activeTab, setActiveTab] = useState('main');
  const [filters, setFilters] = useState<string[]>([]);
  const [mapSrc, setMapSrc] = useState<string>('');
  const [loaded, setLoaded] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterColumn, setFilterColumn] = useState('');
  const [showPlannedDialog, setShowPlannedDialog] = useState(false);
  const [showFollowedUpDialog, setShowFollowedUpDialog] = useState(false);
  const [showTenderingManagerDialog, setShowTenderingManagerDialog] = useState(false);
  const [showStatusesDialog, setShowStatusesDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showNewNoteDialog, setShowNewNoteDialog] = useState(false);
  const [noteActionType, setNoteActionType] = useState<'hide' | 'block' | 'prio' | 'archive' | null>(null);

  const tagOptions: Tag[] = tags ? tags.filter(t => !t.isSystemTag) : [];

  useEffect(() => {
    if (initialTender && initialTenderManagerTool) {
      loadData();
    }
  }, [initialTender]);

  const loadData = async () => {
    setTenderManagerTool(initialTenderManagerTool || null);

    try {
      const [tenderData, systemOptions] = await Promise.all([
        getTenderDocument(initialTender!.tenderID, []),
        getSystemOptionsByID(1),
      ]);
      setTender(tenderData);
      setTenderName(tenderData.tenderName);
      setSystemPassword(systemOptions.systemPassword);
      setCoordinates(tenderData);
      setLoaded(true);
    } catch (error) {
      console.error(error);
      setErrorMessage('Kunde inte hämta verksamhetens uppgifter.');
    }
  };

  const setCoordinates = (tenderData: Tender) => {
    if (!tendering) return;
    
    const cords = tenderData.tenderAnswers?.find(
      q => q.questionID === tendering.properties.questionMappings.coordinates
    )?.value;
    
    if (cords) {
      const cordsArray = cords.split(',');
      const src = `https://maps.google.com/maps?z=7&t=m&q=loc:${cordsArray[0]}+${cordsArray[1]}&ie=UTF8&iwloc=&output=embed`;
      setMapSrc(src);
    }
  };

  const saveTenderingManagerTool = async (updatedTool?: TenderManagerToolModel) => {
    const toolToSave = updatedTool || tenderManagerTool;
    if (!toolToSave) return;
    
    try {
      await updateTenderManagerTool(toolToSave);
      console.log('Saving tender manager tool:', toolToSave);
      setTender(prev => (prev ? { ...prev, tenderManagerTool: toolToSave } : prev));
      onTenderChange?.({ tenderManagerTool: toolToSave });
      // Notify parent of changes
      if (onChange) {
        onChange(toolToSave);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Kunde inte spara ändringarna på verksamheten.');
    }
  };

  const handlePlannedSave = (data: { date: string; text: string }) => {
    if (tenderManagerTool) {
      const updatedTool = {
        ...tenderManagerTool,
        planned: data,
      };
      setTenderManagerTool(updatedTool);
      console.log(data);
      saveTenderingManagerTool(updatedTool);
    }
    setShowPlannedDialog(false);
  };

  const handleFollowedUpSave = (data: { date: string; text: string }) => {
    if (tenderManagerTool) {
      const updatedTool = {
        ...tenderManagerTool,
        followedUp: data,
      };
      setTenderManagerTool(updatedTool);
      saveTenderingManagerTool(updatedTool);
    }
    setShowFollowedUpDialog(false);
  };

  const handleTenderingManagerSave = (managerIds: string[]) => {
    if (tenderManagerTool) {
      const updatedTool = {
        ...tenderManagerTool,
        tenderingManager: managerIds.map(id => parseInt(id)),
      };
      setTenderManagerTool(updatedTool);
      saveTenderingManagerTool(updatedTool);
    }
    setShowTenderingManagerDialog(false);
  };

  const handlePrioSave = () => {
    setNoteActionType('prio');
    setShowNewNoteDialog(true);
  };

  const handleStatusesSave = (statusIds: number[]) => {
    if (tenderManagerTool) {
      const updatedTool = {
        ...tenderManagerTool,
        statuses: statusIds,
      };
      setTenderManagerTool(updatedTool);
      saveTenderingManagerTool(updatedTool);
    }
    setShowStatusesDialog(false);
  };

  const handleNewNoteSave = async (note: any) => {
    try {
      // Create the note
      const noteResult = await createNote(tender!.tenderID, note);
      console.log('Creating note:', noteResult);

      // Perform the action based on type
      if (noteActionType === 'hide' && tender && tenderManagerTool) {
        // The backend toggles module.isHidden + tenderProperties.hidden + tenderManagerTool.statuses
        // atomically as part of creating a "Synlighet" note, so re-fetch the result instead of
        // guessing the new state on the client (avoids drifting out of sync with the server).
        // Note: GetTenderDocument (backend) always projects "content.tenderProperties" itself, so
        // requesting it again here causes a "Duplicate element name" 400 - only ask for the rest.
        const refreshed = await getTenderDocument(tender.tenderID, ['content.tenderManagerTool']);

        const updatedTender = {
          ...tender,
          tenderProperties: { ...tender.tenderProperties, hidden: refreshed.tenderProperties.hidden },
        };
        const updatedTool = {
          ...tenderManagerTool,
          statuses: refreshed.tenderManagerTool?.statuses ?? tenderManagerTool.statuses,
          hidden: refreshed.tenderProperties.hidden,
        };

        setTender({ ...updatedTender, tenderManagerTool: updatedTool });
        setTenderManagerTool(updatedTool);
        // Drawer tabs PUT the whole tender document, so it must see the new hidden state
        onTenderChange?.({ tenderProperties: updatedTender.tenderProperties, tenderManagerTool: updatedTool });
        if (onChange) {
          onChange(updatedTool);
        }
      } else if (noteActionType === 'block' && tenderManagerTool) {
        await authService.refresh();
        const blockedTag = tags?.find(t => t.tagName === 'Blockerad');
        const blockedID = blockedTag?.tagID || 0;

        const module: Module = await getModule(tenderManagerTool.tenderID);

        let moduleToUpdate = { ...module, isBlocked: !tenderManagerTool.statuses.includes(blockedID) };

        await updateModule(moduleToUpdate.moduleID, moduleToUpdate);
        const updatedStatuses = tenderManagerTool.statuses.includes(blockedID)
          ? tenderManagerTool.statuses.filter(s => s !== blockedID)
          : [...tenderManagerTool.statuses, blockedID];

        const updatedTool = {
          ...tenderManagerTool,
          statuses: updatedStatuses,
        };

        setTenderManagerTool(updatedTool);

        await saveTenderingManagerTool(updatedTool);
      } else if (noteActionType === 'prio' && tenderManagerTool) {
        // Update prio from note data
        if (note.prio) {
          const updatedTool = {
            ...tenderManagerTool,
            prio: { prio: note.prio, text: note.noteBody },
          };
          setTenderManagerTool(updatedTool);
          await saveTenderingManagerTool(updatedTool);
        }
      } else if (noteActionType === 'archive' && tender) {
        const updatedArchivingUsers = [...(tender.archivingUsers || []), user?.user?.userID];
        const updatedTender = { ...tender, archivingUsers: updatedArchivingUsers };
        await updateTenderDocument(tender.tenderID, updatedTender);
        setTender(updatedTender);
        onTenderChange?.({ archivingUsers: updatedArchivingUsers });
      }

      setShowNewNoteDialog(false);
      setNoteActionType(null);
    } catch (error: any) {
      console.error('Error saving note:', error);
      setErrorMessage(error?.response?.data?.message || 'Åtgärden kunde inte sparas. Försök igen.');
    }
  };

  const handleSaveTenderName = async () => {
    if (!tender) return;
    try {
      const curr = await getTenderDocument(tender.tenderID);
      curr.tenderName = tenderName;
      await updateTenderDocument(tender.tenderID, curr);
      setTender({ ...tender, tenderName });
      onTenderChange?.({ tenderName });
    } catch (error) {
      console.error(error);
      setErrorMessage('Kunde inte spara verksamhetens namn.');
    }
  };

  const getArchiveButtonProps = (): [boolean, string] => {
    if (!tender?.archivingUsers || tender.archivingUsers.length === 0) {
      return [false, 'Avpublicera verksamhet'];
    } else if (tender.archivingUsers.includes(user?.user?.userID)) {
      return [true, 'Inväntar avpublicering'];
    } else {
      return [false, 'Bekräfta avpublicering'];
    }
  };

  const onArchiveClick = () => {
    setPasswordDialogOpen(true);
  };

  const handleArchiveConfirm = async () => {
    if (!tender) return;
    try {
      if (!tender.archivingUsers || tender.archivingUsers.length === 0) {
        const updatedTender = { ...tender, archivingUsers: [user?.user?.userID] };
        await updateTenderDocument(tender.tenderID, updatedTender);
        setTender(updatedTender);
        onTenderChange?.({ archivingUsers: updatedTender.archivingUsers });
        setPasswordDialogOpen(false);
      } else {
        setPasswordDialogOpen(false);
        setNoteActionType('archive');
        setShowNewNoteDialog(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Kunde inte avpublicera verksamheten.');
    }
  };

  const getInitialNoteData = () => {
    if (!noteActionType || !tender || !tenderManagerTool) return undefined;

    const baseData = {
      noteDate: new Date().toISOString(),
      isNotePublic: false,
    };

    if (noteActionType === 'hide') {
      return {
        ...baseData,
        noteSubject: 'Synlighet',
        noteHeader: tender.tenderProperties.hidden 
          ? `${tenderManagerTool.tenderName} synliggörs`
          : `${tenderManagerTool.tenderName} döljs`,
        noteBody: '',
      };
    } else if (noteActionType === 'block') {
      const blockedTag = tags?.find(t => t.tagName === 'Blockerad');
      const isBlocked = tenderManagerTool.statuses.includes(blockedTag?.tagID || 0);
      return {
        ...baseData,
        noteSubject: 'Blockering massutskick',
        noteHeader: isBlocked
          ? `${tenderManagerTool.tenderName} avblockering`
          : `${tenderManagerTool.tenderName} blockering`,
        noteBody: '',
      };
    } else if (noteActionType === 'prio') {
      return {
        ...baseData,
        noteSubject: 'Prio',
        noteHeader: `${tenderManagerTool.tenderName} Prio`,
        noteBody: tenderManagerTool.prio?.text || '',
      };
    } else if (noteActionType === 'archive') {
      return {
        ...baseData,
        noteSubject: 'Verksamhet avpublicering',
        noteHeader: `${tender?.tenderName} avpublicering`,
        noteBody: '',
      };
    }

    return undefined;
  };

  const getUserName = (id: string | number): string => {
    const user = users?.find(u => u.userID === id || u.userID === parseInt(id.toString()));
    return user ? `${user.firstName} ${user.lastName}` : '';
  };

  const getStatusName = (id: number): string => {
    const tag = tags?.find(t => t.tagID === id);
    return tag ? tag.tagName : '';
  };

  const isTenderBlocked = (): boolean => {
    const blockedTag = tags?.find(t => t.tagName === 'Blockerad');
    return tenderManagerTool?.statuses?.includes(blockedTag?.tagID || 0) || false;
  };

  const getHeaderText = (): { icon: string; text: string } => {
    if (!tender) return { icon: '', text: '' };
    
    if (tender.tenderProperties?.unpublished) {
      return { icon: 'error', text: 'Verksamheten är avpublicerad!' };
    } else if (tender.tenderProperties?.hidden) {
      return { icon: 'visibility_off', text: 'Verksamheten är dold!' };
    }
    return { icon: '', text: '' };
  };

  const handleHideClick = () => {
    setNoteActionType('hide');
    setShowNewNoteDialog(true);
  };

  const handleBlockClick = () => {
    setNoteActionType('block');
    setShowNewNoteDialog(true);
  };

  const handleFilterModalClose = (filterValue?: string) => {
    if (filterValue) {
      setFilters([...filters, filterValue]);
      // Notify parent to add filter to main list
      if (onFilterAdd) {
        onFilterAdd(filterValue);
      }
    }
    setShowFilterModal(false);
  };

  const handleClose = () => {
    // onDismiss(filters);
  };

  if (!tenderManagerTool) return null;

  const headerInfo = getHeaderText();

  return (
    <>
      <div className="w-full h-full overflow-y-auto">
        {!loaded ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Spinner size="large" />
          </div>
        ) : (
          <div className="p-6">
              {errorMessage && (
                <MessageBar intent="error" className="mb-4">
                  <MessageBarBody>{errorMessage}</MessageBarBody>
                </MessageBar>
              )}
              {activeTab === 'main' && (
                <div className="mt-2">
                  {/* Tender Name */}
                  <div className="flex gap-3 items-end mb-6">
                    <div className="flex-1">
                      <h5 className="text-sm font-semibold mb-2">Verksamhetens namn</h5>
                      <Input
                        className="w-full"
                        value={tenderName}
                        onChange={(e) => setTenderName(e.target.value)}
                      />
                    </div>
                    <Button appearance="primary" onClick={handleSaveTenderName}>
                      Spara namn
                    </Button>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <Button
                      appearance="secondary"
                      icon={<PhoneRegular />}
                      onClick={() => setShowContactModal(true)}
                    >
                      Kontakta leverantören
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={tender?.tenderProperties?.hidden ? <EyeRegular /> : <EyeOffRegular />}
                      onClick={handleHideClick}
                    >
                      {tender?.tenderProperties?.hidden ? 'Avdölj leverantören' : 'Dölj leverantören'}
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={isTenderBlocked() ? <Speaker2Regular /> : <SpeakerOffRegular />}
                      onClick={handleBlockClick}
                    >
                      {isTenderBlocked() ? 'Avblockera massutskick' : 'Blockera massutskick'}
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={<EditRegular />}
                      onClick={() => setActiveTab('log')}
                    >
                      Skapa loggboksanteckning
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={<PaymentRegular />}
                      onClick={() => window.open('https://www.creditsafe.com/se/sv.html', '_blank')}
                    >
                      CreditSafe
                    </Button>
                    <Button
                      appearance="secondary"
                      icon={<SearchRegular />}
                      onClick={() => window.open('https://www.ivo.se/', '_blank')}
                    >
                      Tillstånd hos IVO
                    </Button>
                    {(!tender?.archivingUsers || tender.archivingUsers.length < 2) && (
                      <Button
                        className="col-span-2"
                        appearance="primary"
                        icon={<ArchiveRegular />}
                        disabled={getArchiveButtonProps()[0]}
                        onClick={onArchiveClick}
                        style={getArchiveButtonProps()[0] ? undefined : { background: 'var(--colorPaletteRedBackground3)' }}
                      >
                        {getArchiveButtonProps()[1]}
                      </Button>
                    )}
                  </div>

                  {/* Map */}
                  {mapSrc && (
                    <div className="mb-6">
                      <iframe
                        width="100%"
                        height="200"
                        src={mapSrc}
                        frameBorder="0"
                        scrolling="no"
                        className="border-0 rounded"
                      />
                    </div>
                  )}

                  {/* Settings */}
                  <div className="space-y-4">
                    <SettingItem
                      title="Planerad"
                      value={`${tenderManagerTool.planned?.date || ''} ${tenderManagerTool.planned?.text || ''}`}
                      onEdit={() => setShowPlannedDialog(true)}
                      onFilter={() => { setFilterColumn('Planerad'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Uppföljd"
                      value={`${tenderManagerTool.followedUp?.date || ''} ${tenderManagerTool.followedUp?.text || ''}`}
                      onEdit={() => setShowFollowedUpDialog(true)}
                      onFilter={() => { setFilterColumn('Uppföljd'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Handläggare"
                      value={tenderManagerTool.tenderingManager?.map(id => getUserName(id)).join(', ')}
                      onEdit={() => setShowTenderingManagerDialog(true)}
                      onFilter={() => { setFilterColumn('Handläggare'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Ramavtal"
                      value={tenderManagerTool.tendering}
                      onFilter={() => { setFilterColumn('Ramavtal'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Avtalspart"
                      value={tenderManagerTool.tenderName}
                      onFilter={() => { setFilterColumn('Avtalspart'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Koncern"
                      value={tenderManagerTool.organization}
                      onFilter={() => { setFilterColumn('Koncern'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Org. nr."
                      value={tenderManagerTool.organizationNr?.toString()}
                      onFilter={() => { setFilterColumn('Org.nr'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Besöksadress"
                      value={tenderManagerTool.adress}
                      onFilter={() => { setFilterColumn('Besöksadress'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Kontaktperson"
                      value={tenderManagerTool.contactName}
                      onFilter={() => { setFilterColumn('Kontaktperson'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Prio"
                      value={tenderManagerTool.prio?.prio?.toString() || ''}
                      onEdit={() => handlePrioSave()}
                      onFilter={() => { setFilterColumn('Prio'); setShowFilterModal(true); }}
                    />

                    <SettingItem
                      title="Statusetiketter"
                      value={tenderManagerTool.statuses?.map(id => getStatusName(id)).join(', ')}
                      onEdit={() => setShowStatusesDialog(true)}
                      onFilter={() => { setFilterColumn('Statusetiketter'); setShowFilterModal(true); }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      {/* Modals */}
      {showContactModal && tender && tags && (
        <TenderContactDrawer
          isOpen={showContactModal}
          onDismiss={() => setShowContactModal(false)}
          tender={tender}
          tendering={tendering}
          tags={tags}
        />
      )}

      {showFilterModal && (
        <TenderingManagerFilterDialog
          onClose={() => setShowFilterModal(false)}
          open={showFilterModal}
          onDismiss={handleFilterModalClose}
          column={filterColumn}
        />
      )}

      {showPlannedDialog && tenderManagerTool && (
        <PlannedDialog
          isOpen={showPlannedDialog}
          onDismiss={() => setShowPlannedDialog(false)}
          onSave={handlePlannedSave}
          initialData={tenderManagerTool.planned}
        />
      )}

      {showFollowedUpDialog && tenderManagerTool && (
        <FollowedUpDialog
          isOpen={showFollowedUpDialog}
          onDismiss={() => setShowFollowedUpDialog(false)}
          onSave={handleFollowedUpSave}
          initialData={tenderManagerTool.followedUp}
        />
      )}

      {showTenderingManagerDialog && tenderManagerTool && users && (
        <TenderingManagerDialog
          isOpen={showTenderingManagerDialog}
          onDismiss={() => setShowTenderingManagerDialog(false)}
          onSave={handleTenderingManagerSave}
          users={users}
          selectedManagers={tenderManagerTool.tenderingManager?.map(id => id.toString()) || []}
        />
      )}

      {/* {showPrioDialog && tenderManagerTool && (
        <PrioDialog
          isOpen={showPrioDialog}
          onDismiss={() => setShowPrioDialog(false)}
          onSave={handlePrioSave}
          initialData={tenderManagerTool.prio}
        />
      )} */}

      {showStatusesDialog && tenderManagerTool && (
        <StatusesDialog
          isOpen={showStatusesDialog}
          onDismiss={() => setShowStatusesDialog(false)}
          onSave={handleStatusesSave}
          tags={tagOptions}
          selectedStatuses={tenderManagerTool.statuses}
        />
      )}

      {passwordDialogOpen && tender && (
        <PasswordDialog
          isOpen={passwordDialogOpen}
          onClose={() => setPasswordDialogOpen(false)}
          onConfirm={handleArchiveConfirm}
          title={`Vill du avpublicera ${tender.tenderName}?`}
          user={user?.user}
          presetPassword={systemPassword}
        />
      )}

      {showNewNoteDialog && (
        <NewNote
          open={showNewNoteDialog}
          onClose={() => {
            setShowNewNoteDialog(false);
            setNoteActionType(null);
          }}
          onSave={handleNewNoteSave}
          initialData={getInitialNoteData()}
          actionType={noteActionType}
        />
      )}
    </>
  );
};

interface SettingItemProps {
  title: string;
  value?: string;
  onEdit?: () => void;
  onFilter?: () => void;
}

const SettingItem: React.FC<SettingItemProps> = ({ title, value, onEdit, onFilter }) => (
  <div className="border-b border-gray-200 pb-3">
    <div className="flex justify-between items-center mb-2">
      <h5 className="font-semibold text-base">{title}</h5>
      <div className="flex gap-2">
        {onEdit && (
          <Button
            appearance="subtle"
            size="small"
            icon={<EditRegular />}
            onClick={onEdit}
            aria-label={`Redigera ${title}`}
          />
        )}
        {onFilter && (
          <Button
            appearance="subtle"
            size="small"
            icon={<FilterRegular />}
            onClick={onFilter}
            aria-label="Filter"
          />
        )}
      </div>
    </div>
    <div className="text-sm text-gray-700">{value || '-'}</div>
  </div>
);
